import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Download,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface ProductRow {
  brandCode: string;
  modelCode: string;
  productCode?: string;
  sku?: string;
  name: string;
  shortDescription?: string;
  fullDescription?: string;
  price?: number;
  currency?: string;
  priceOnRequest?: boolean;
  stockStatus?: string;
  minOrderQty?: number;
  isActive?: boolean;
  isFeatured?: boolean;
}

interface ParsedData {
  rows: ProductRow[];
  errors: Array<{ row: number; message: string }>;
}

interface ProductImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Simple CSV parser
function parseCSV(csvText: string): ParsedData {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    return { rows: [], errors: [{ row: 0, message: 'CSV file is empty or has no data rows' }] };
  }

  const headerLine = lines[0];
  if (!headerLine) {
    return { rows: [], errors: [{ row: 0, message: 'CSV file has no header row' }] };
  }
  const headers = headerLine.split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));
  const rows: ProductRow[] = [];
  const errors: Array<{ row: number; message: string }> = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]?.trim();
    if (!line) continue;

    // Simple CSV parsing (handles quoted values with commas)
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      if (char === '"' && (j === 0 || line[j - 1] !== '\\')) {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim().replace(/^["']|["']$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim().replace(/^["']|["']$/g, ''));

    // Map to object
    const rowObj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rowObj[h] = values[idx] || '';
    });

    // Validate required fields
    if (!rowObj.brandCode) {
      errors.push({ row: i + 1, message: 'Missing brandCode' });
      continue;
    }
    if (!rowObj.modelCode) {
      errors.push({ row: i + 1, message: 'Missing modelCode' });
      continue;
    }
    if (!rowObj.name) {
      errors.push({ row: i + 1, message: 'Missing name' });
      continue;
    }

    // Parse row
    const parsedRow: ProductRow = {
      brandCode: rowObj.brandCode,
      modelCode: rowObj.modelCode,
      productCode: rowObj.productCode || undefined,
      sku: rowObj.sku || undefined,
      name: rowObj.name,
      shortDescription: rowObj.shortDescription || undefined,
      fullDescription: rowObj.fullDescription || undefined,
      price: rowObj.price ? parseFloat(rowObj.price) : undefined,
      currency: rowObj.currency || 'EUR',
      priceOnRequest: rowObj.priceOnRequest?.toLowerCase() === 'true',
      stockStatus: rowObj.stockStatus || 'IN_STOCK',
      minOrderQty: rowObj.minOrderQty ? parseInt(rowObj.minOrderQty, 10) : 1,
      isActive: rowObj.isActive ? rowObj.isActive.toLowerCase() !== 'false' : true,
      isFeatured: rowObj.isFeatured?.toLowerCase() === 'true',
    };

    rows.push(parsedRow);
  }

  return { rows, errors };
}

export function ProductImportModal({ open, onOpenChange }: ProductImportModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [createMissingModels, setCreateMissingModels] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{ row: number; error: string }>;
    total: number;
  } | null>(null);

  const utils = trpc.useUtils();
  const { data: templateData } = trpc.tradingProduct.getImportTemplate.useQuery();

  const importMutation = trpc.tradingProduct.bulkImport.useMutation({
    onSuccess: (result) => {
      setImportResult(result);
      if (result.success) {
        showSuccessToast(`Successfully imported ${result.created} products`);
        utils.tradingProduct.list.invalidate();
      }
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const csvFile = acceptedFiles[0];
    if (!csvFile) return;

    setFile(csvFile);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      setParsedData(parsed);
    };
    reader.readAsText(csvFile);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleClose = () => {
    setFile(null);
    setParsedData(null);
    setImportResult(null);
    setShowPreview(false);
    onOpenChange(false);
  };

  const handleImport = () => {
    if (!parsedData || parsedData.rows.length === 0) return;

    importMutation.mutate({
      products: parsedData.rows.map((row) => ({
        brandCode: row.brandCode,
        modelCode: row.modelCode,
        productCode: row.productCode,
        sku: row.sku,
        name: row.name,
        shortDescription: row.shortDescription,
        fullDescription: row.fullDescription,
        price: row.price,
        currency: row.currency || 'EUR',
        priceOnRequest: row.priceOnRequest || false,
        stockStatus: (row.stockStatus as any) || 'IN_STOCK',
        minOrderQty: row.minOrderQty || 1,
        isActive: row.isActive ?? true,
        isFeatured: row.isFeatured || false,
      })),
      updateExisting,
      createMissingModels,
    });
  };

  const downloadTemplate = () => {
    if (!templateData) return;

    const headers = templateData.columns.map((c) => c.name).join(',');
    const sampleRow = templateData.columns
      .map((c) => {
        const value = templateData.sampleRow[c.name as keyof typeof templateData.sampleRow];
        if (typeof value === 'string') {
          return `"${value}"`;
        }
        return value;
      })
      .join(',');

    const csvContent = `${headers}\n${sampleRow}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'product_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {t('products.importProducts')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Import Result */}
          {importResult && (
            <div
              className={`rounded-lg border p-4 ${
                importResult.success
                  ? 'bg-green-50 border-green-200'
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {importResult.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className="font-medium">
                    {importResult.success ? 'Import Completed' : 'Import Completed with Issues'}
                  </h4>
                  <div className="mt-2 grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total:</span>{' '}
                      <span className="font-medium">{importResult.total}</span>
                    </div>
                    <div>
                      <span className="text-green-600">Created:</span>{' '}
                      <span className="font-medium">{importResult.created}</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Updated:</span>{' '}
                      <span className="font-medium">{importResult.updated}</span>
                    </div>
                    <div>
                      <span className="text-amber-600">Skipped:</span>{' '}
                      <span className="font-medium">{importResult.skipped}</span>
                    </div>
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-red-600">
                        Errors ({importResult.errors.length}):
                      </p>
                      <ul className="mt-1 text-sm text-red-600 list-disc list-inside max-h-24 overflow-y-auto">
                        {importResult.errors.slice(0, 10).map((err, idx) => (
                          <li key={idx}>
                            Row {err.row}: {err.error}
                          </li>
                        ))}
                        {importResult.errors.length > 10 && (
                          <li>...and {importResult.errors.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* File Drop Zone */}
          {!importResult && (
            <>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                {isDragActive ? (
                  <p className="text-primary font-medium">Drop the CSV file here...</p>
                ) : (
                  <>
                    <p className="font-medium">
                      Drag & drop a CSV file here, or click to select
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Only .csv files are accepted
                    </p>
                  </>
                )}
              </div>

              {/* Template Download */}
              <div className="flex items-center justify-between text-sm">
                <p className="text-muted-foreground">
                  Need a template? Download our CSV template with all required columns.
                </p>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </>
          )}

          {/* File Info & Parse Errors */}
          {file && parsedData && !importResult && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {parsedData.rows.length} valid rows
                      {parsedData.errors.length > 0 && (
                        <span className="text-amber-600 ml-2">
                          ({parsedData.errors.length} parse errors)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setParsedData(null);
                  }}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>

              {/* Parse Errors */}
              {parsedData.errors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    Parse Warnings ({parsedData.errors.length})
                  </p>
                  <ul className="text-sm text-amber-700 list-disc list-inside max-h-20 overflow-y-auto">
                    {parsedData.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>
                        Row {err.row}: {err.message}
                      </li>
                    ))}
                    {parsedData.errors.length > 5 && (
                      <li>...and {parsedData.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Import Options */}
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium">Import Options</h4>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="updateExisting"
                    checked={updateExisting}
                    onCheckedChange={(checked) => setUpdateExisting(checked === true)}
                  />
                  <Label htmlFor="updateExisting" className="text-sm cursor-pointer">
                    Update existing products (match by SKU or code)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="createMissingModels"
                    checked={createMissingModels}
                    onCheckedChange={(checked) => setCreateMissingModels(checked === true)}
                  />
                  <Label htmlFor="createMissingModels" className="text-sm cursor-pointer">
                    Create missing models automatically
                  </Label>
                </div>
              </div>

              {/* Preview Toggle */}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                {showPreview ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                {showPreview ? 'Hide Preview' : 'Show Preview'} ({parsedData.rows.length} rows)
              </button>

              {/* Data Preview */}
              {showPreview && parsedData.rows.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-64">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">#</th>
                          <th className="px-3 py-2 text-left font-medium">Brand</th>
                          <th className="px-3 py-2 text-left font-medium">Model</th>
                          <th className="px-3 py-2 text-left font-medium">Code</th>
                          <th className="px-3 py-2 text-left font-medium">SKU</th>
                          <th className="px-3 py-2 text-left font-medium">Name</th>
                          <th className="px-3 py-2 text-left font-medium">Price</th>
                          <th className="px-3 py-2 text-left font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {parsedData.rows.slice(0, 20).map((row, idx) => (
                          <tr key={idx} className="hover:bg-muted/50">
                            <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                            <td className="px-3 py-2 font-mono">{row.brandCode}</td>
                            <td className="px-3 py-2 font-mono">{row.modelCode}</td>
                            <td className="px-3 py-2 font-mono">{row.productCode || '-'}</td>
                            <td className="px-3 py-2">{row.sku || '-'}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate">{row.name}</td>
                            <td className="px-3 py-2">
                              {row.priceOnRequest
                                ? 'On Request'
                                : row.price
                                ? `${row.currency || 'EUR'} ${row.price}`
                                : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                                  row.stockStatus === 'IN_STOCK'
                                    ? 'bg-green-100 text-green-700'
                                    : row.stockStatus === 'LOW_STOCK'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : row.stockStatus === 'OUT_OF_STOCK'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {row.stockStatus || 'IN_STOCK'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsedData.rows.length > 20 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center bg-muted/50">
                        Showing first 20 of {parsedData.rows.length} rows
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Column Reference */}
          {!file && templateData && (
            <div className="text-sm">
              <h4 className="font-medium mb-2">Required Columns</h4>
              <div className="grid grid-cols-2 gap-2">
                {templateData.columns
                  .filter((c) => c.required)
                  .map((col) => (
                    <div key={col.name} className="flex items-center gap-2">
                      <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                        {col.name}
                      </code>
                      <span className="text-muted-foreground text-xs">{col.description}</span>
                    </div>
                  ))}
              </div>
              <h4 className="font-medium mb-2 mt-4">Optional Columns</h4>
              <div className="grid grid-cols-2 gap-2">
                {templateData.columns
                  .filter((c) => !c.required)
                  .map((col) => (
                    <div key={col.name} className="flex items-center gap-2">
                      <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                        {col.name}
                      </code>
                      <span className="text-muted-foreground text-xs">{col.description}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {importResult ? tCommon('close') : tCommon('cancel')}
          </Button>
          {!importResult && parsedData && parsedData.rows.length > 0 && (
            <Button onClick={handleImport} disabled={importMutation.isPending}>
              {importMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Import {parsedData.rows.length} Products
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
